import { useMemo } from "react";
const calculateMedian = (values) => {
    if (values.length === 0)
        return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};
const formatDuration = (seconds) => {
    if (seconds === 0)
        return "0s";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.round(seconds % 60);
    const parts = [];
    if (hours > 0)
        parts.push(`${hours}h`);
    if (minutes > 0)
        parts.push(`${minutes}m`);
    if (remainingSeconds > 0 || parts.length === 0)
        parts.push(`${remainingSeconds}s`);
    return parts.join(" ");
};
const URGENT_PRIORITIES = ["Urgent", "Stat", "Emergent", "Urgent Immediate"];
const ROUTINE_PRIORITIES = ["Routine", "Routine Morning", "Routine Next Business Day", "FYI"];
export const useInteractionMetrics = (filteredInteractions, allInteractions) => {
    return useMemo(() => {
        const totalInteractions = filteredInteractions.length;
        const urgent = filteredInteractions.filter((i) => URGENT_PRIORITIES.includes(i.ToFilterPriority || "")).length;
        const routine = filteredInteractions.filter((i) => ROUTINE_PRIORITIES.includes(i.ToFilterPriority || "")).length;
        const uniqueAccounts = new Set(filteredInteractions.map((i) => i.ToFilterObjectAccountId));
        const uniquePractices = new Set(filteredInteractions.map((i) => i.ToFilterObjectName));
        const urgentInteractions = filteredInteractions.filter((i) => URGENT_PRIORITIES.includes(i.ToFilterPriority || "") && i.SecureMessageReadUtc && i.SecureMessageSentUtc);
        const callbackInteractions = filteredInteractions.filter((i) => i.CallbackFromMessageIdUtc && i.SecureMessageSentUtc);
        const avgUrgentReadTime = urgentInteractions.length
            ? Math.round(urgentInteractions.reduce((sum, i) => {
                const sent = new Date(i.SecureMessageSentUtc);
                const read = new Date(i.SecureMessageReadUtc);
                return sum + (read.getTime() - sent.getTime()) / 1000;
            }, 0) / urgentInteractions.length)
            : 0;
        const medianUrgentReadTime = urgentInteractions.length
            ? calculateMedian(urgentInteractions.map((i) => {
                const sent = new Date(i.SecureMessageSentUtc);
                const read = new Date(i.SecureMessageReadUtc);
                return (read.getTime() - sent.getTime()) / 1000;
            }))
            : 0;
        const avgCallbackTime = callbackInteractions.length
            ? Math.round(callbackInteractions.reduce((sum, i) => {
                const sent = new Date(i.SecureMessageSentUtc);
                const callback = new Date(i.CallbackFromMessageIdUtc);
                return sum + (callback.getTime() - sent.getTime()) / 1000;
            }, 0) / callbackInteractions.length)
            : 0;
        const medianCallbackTime = callbackInteractions.length
            ? calculateMedian(callbackInteractions.map((i) => {
                const sent = new Date(i.SecureMessageSentUtc);
                const callback = new Date(i.CallbackFromMessageIdUtc);
                return (callback.getTime() - sent.getTime()) / 1000;
            }))
            : 0;
        const callbackDurationInteractions = filteredInteractions.filter((i) => i.CallbackDurationSeconds && i.CallbackDurationSeconds > 0);
        const avgCallbackDuration = callbackDurationInteractions.length
            ? Math.round(callbackDurationInteractions.reduce((sum, i) => sum + (i.CallbackDurationSeconds || 0), 0) /
                callbackDurationInteractions.length)
            : 0;
        const medianCallbackDuration = callbackDurationInteractions.length
            ? calculateMedian(callbackDurationInteractions.map((i) => i.CallbackDurationSeconds || 0))
            : 0;
        const callbackCount = filteredInteractions.filter((i) => i.CallbackFromMessageIdCount && i.CallbackFromMessageIdCount > 0).length;
        const voicemails = filteredInteractions.filter((i) => i.InteractionFinalAction === "VoiceMessage").length;
        const transfers = filteredInteractions.filter((i) => {
            const action = i.InteractionFinalAction || "";
            const normalizedAction = action.replace(/\s+/g, "");
            return (normalizedAction === "BlindTransfer" ||
                normalizedAction === "ControlledTransfer" ||
                normalizedAction === "HandoffTransfer");
        }).length;
        const isBackup = allInteractions.filter((i) => i.IsBackup).length;
        const isForward = allInteractions.filter((i) => i.IsForwarded).length;
        return {
            accounts: uniqueAccounts.size,
            practices: uniquePractices.size,
            totalInteractions,
            urgent,
            routine,
            avgUrgentReadTime: formatDuration(avgUrgentReadTime),
            medianUrgentReadTime: formatDuration(medianUrgentReadTime),
            avgCallbackTime: formatDuration(avgCallbackTime),
            medianCallbackTime: formatDuration(medianCallbackTime),
            avgCallbackDuration: formatDuration(avgCallbackDuration),
            medianCallbackDuration: formatDuration(medianCallbackDuration),
            avgUrgentReadTimeSeconds: avgUrgentReadTime,
            medianUrgentReadTimeSeconds: medianUrgentReadTime,
            avgCallbackTimeSeconds: avgCallbackTime,
            medianCallbackTimeSeconds: medianCallbackTime,
            avgCallbackDurationSeconds: avgCallbackDuration,
            medianCallbackDurationSeconds: medianCallbackDuration,
            callbackCount,
            voicemails,
            transfers,
            isBackup,
            isForward,
        };
    }, [filteredInteractions, allInteractions]);
};
//# sourceMappingURL=useInteractionMetrics.js.map